/**
 * Where an order came from (ADR-097): the visit's source as the storefront
 * keeps it, the channel the API classifies it into, and the report over both.
 * Pure, so the storefront, the API and the admin read a visit the same way.
 */
export const TRAFFIC_CHANNELS = [
    'paid_search',
    'paid_social',
    'organic_search',
    'social',
    'email',
    'sms',
    'affiliate',
    'referral',
    'other',
    'direct',
    /** Placed by the team for a customer — on the phone, in a chat (ADR-062). */
    'staff',
];
/** An ad click's id, by the parameter its platform adds to the link. */
export const CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid', 'msclkid', 'ttclid', 'ScCid', 'fbclid'];
/** The `utm_*` tags a link carries. */
export const UTM_FIELDS = ['source', 'medium', 'campaign', 'term', 'content'];
/** The longest a kept value may be: tags are labels, not essays. */
export const ATTRIBUTION_LIMITS = { tag: 100, campaign: 150, referrer: 120, landing: 200 };
const SEARCH_HOSTS = ['google', 'bing', 'yahoo', 'yandex', 'duckduckgo', 'baidu', 'ecosia', 'naver', 'brave'];
const SEARCH_SOURCES = new Set(SEARCH_HOSTS);
const SOCIAL_HOSTS = [
    'facebook.com',
    'fb.com',
    'fb.me',
    'instagram.com',
    't.co',
    'twitter.com',
    'x.com',
    'tiktok.com',
    'snapchat.com',
    'linkedin.com',
    'lnkd.in',
    'pinterest.com',
    'youtube.com',
    'youtu.be',
    'reddit.com',
    'whatsapp.com',
    'wa.me',
    't.me',
    'telegram.org',
    'threads.net',
];
const SOCIAL_SOURCES = new Set([
    'facebook', 'fb', 'instagram', 'ig', 'twitter', 'x', 'tiktok', 'snapchat', 'snap', 'linkedin', 'pinterest',
    'youtube', 'reddit', 'whatsapp', 'telegram', 'threads', 'meta',
]);
const EMAIL_HOSTS = ['mail.google.com', 'outlook.live.com', 'outlook.office.com', 'mail.yahoo.com'];
const PAID_MEDIUMS = new Set(['cpc', 'ppc', 'paid', 'paidsearch', 'paid_search', 'paid-search', 'cpm', 'cpv', 'display', 'ad', 'ads', 'paid_social', 'paidsocial', 'paid-social', 'sponsored']);
const PAID_SOCIAL_MEDIUMS = new Set(['paid_social', 'paidsocial', 'paid-social']);
/** A host's own name: `www.google.com.sa` → is it `google`? */
function hostIs(host, name) {
    return host === name || host.endsWith(`.${name}`);
}
function searchHost(host) {
    return SEARCH_HOSTS.some((name) => host.split('.').includes(name));
}
/**
 * The channel a touch belongs to (ADR-097). The tags a link carries come
 * first — they are what the store chose to say — then an ad's click id, then
 * the site that sent the visit; with none, the order is direct.
 */
export function trafficChannel(touch) {
    if (!touch)
        return 'direct';
    const source = touch.source?.toLowerCase() ?? '';
    const medium = touch.medium?.toLowerCase() ?? '';
    const referrer = touch.referrer?.toLowerCase() ?? '';
    if (medium === 'affiliate')
        return 'affiliate';
    if (['email', 'e-mail', 'newsletter'].includes(medium))
        return 'email';
    if (['sms', 'text', 'whatsapp_broadcast'].includes(medium))
        return 'sms';
    if (PAID_MEDIUMS.has(medium)) {
        if (PAID_SOCIAL_MEDIUMS.has(medium) || SOCIAL_SOURCES.has(source))
            return 'paid_social';
        return 'paid_search';
    }
    if (touch.clickId === 'gclid' || touch.clickId === 'gbraid' || touch.clickId === 'wbraid' || touch.clickId === 'msclkid')
        return 'paid_search';
    if (touch.clickId === 'ttclid' || touch.clickId === 'ScCid')
        return 'paid_social';
    if (['social', 'social-media', 'social_media', 'sm'].includes(medium) || SOCIAL_SOURCES.has(source))
        return 'social';
    if (medium === 'organic' && SEARCH_SOURCES.has(source))
        return 'organic_search';
    if (medium === 'referral' && source)
        return 'referral';
    // Meta adds its click id to every link followed from Facebook or Instagram, paid or not.
    if (touch.clickId === 'fbclid')
        return 'social';
    if (referrer) {
        if (EMAIL_HOSTS.some((host) => hostIs(referrer, host)))
            return 'email';
        if (searchHost(referrer))
            return 'organic_search';
        if (SOCIAL_HOSTS.some((host) => hostIs(referrer, host)))
            return 'social';
        return 'referral';
    }
    if (source || touch.campaign)
        return 'other';
    return 'direct';
}
function clipped(value, max, lower = false) {
    // Control characters dropped: a tag is a label, and a cookie or a CSV cell is no place for them.
    const text = [...(value ?? '')].filter((char) => char.charCodeAt(0) >= 0x20).join('').trim();
    if (!text)
        return undefined;
    const cut = text.slice(0, max);
    return lower ? cut.toLowerCase() : cut;
}
/** A web address's host, bare; null for anything that is not one. */
function hostOf(address) {
    const match = /^https?:\/\/(?:[^/?#@]*@)?([^/?#:]+)/i.exec(address?.trim() ?? '');
    return match ? bareHost(match[1]) : null;
}
/** Strips `www.` and lower-cases: `WWW.Google.com.sa` → `google.com.sa`. */
function bareHost(host) {
    return host.toLowerCase().replace(/^www\./, '');
}
/**
 * The touch a landing request makes, or null when it says nothing new — a
 * direct visit, or a page of the store itself — and the kept one stands.
 */
export function touchFrom(input) {
    const { params } = input;
    const touch = {
        source: clipped(params.get('utm_source'), ATTRIBUTION_LIMITS.tag, true),
        medium: clipped(params.get('utm_medium'), ATTRIBUTION_LIMITS.tag, true),
        campaign: clipped(params.get('utm_campaign'), ATTRIBUTION_LIMITS.campaign),
        term: clipped(params.get('utm_term'), ATTRIBUTION_LIMITS.tag),
        content: clipped(params.get('utm_content'), ATTRIBUTION_LIMITS.tag),
        clickId: CLICK_ID_PARAMS.find((name) => params.get(name)),
    };
    if (!touch.source && !touch.medium && !touch.campaign && input.affiliate) {
        touch.source = input.affiliate;
        touch.medium = 'affiliate';
    }
    const host = hostOf(input.referrer);
    // The store's own host as a proxy names it: the first of a list, without its port.
    const own = bareHost(input.ownHost.split(',')[0].trim().replace(/:\d+$/, ''));
    const referrer = host && host !== own ? host.slice(0, ATTRIBUTION_LIMITS.referrer) : undefined;
    touch.referrer = referrer;
    if (!touch.source && !touch.medium && !touch.campaign && !touch.clickId && !referrer)
        return null;
    touch.landing = clipped(input.path, ATTRIBUTION_LIMITS.landing);
    touch.at = (input.now ?? new Date()).toISOString();
    return Object.fromEntries(Object.entries(touch).filter(([, value]) => value !== undefined));
}
/** A kept touch read back from its cookie: only the known fields, each within its limit; null for anything else. */
export function readTouch(raw) {
    if (!raw)
        return null;
    try {
        return touchOf(JSON.parse(raw));
    }
    catch {
        return null;
    }
}
/** Any value as a touch — what a checkout sent, what a cookie held: the known fields within their limits, or null. */
export function touchOf(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return null;
    const record = value;
    const text = (key, max, lower = false) => (typeof record[key] === 'string' ? clipped(record[key], max, lower) : undefined);
    const touch = {
        source: text('source', ATTRIBUTION_LIMITS.tag, true),
        medium: text('medium', ATTRIBUTION_LIMITS.tag, true),
        campaign: text('campaign', ATTRIBUTION_LIMITS.campaign),
        term: text('term', ATTRIBUTION_LIMITS.tag),
        content: text('content', ATTRIBUTION_LIMITS.tag),
        clickId: CLICK_ID_PARAMS.find((name) => name === record.clickId),
        referrer: text('referrer', ATTRIBUTION_LIMITS.referrer, true),
        landing: text('landing', ATTRIBUTION_LIMITS.landing),
        at: typeof record.at === 'string' && !Number.isNaN(Date.parse(record.at)) ? record.at : undefined,
    };
    const kept = Object.fromEntries(Object.entries(touch).filter(([, value]) => value !== undefined));
    return Object.keys(kept).length > 0 ? kept : null;
}
// ─── Campaign links ──────────────────────────────────────────────────────────
/** Where a tagged link is posted, with the source and medium it is filed under. */
export const CAMPAIGN_PRESETS = [
    { key: 'instagram', source: 'instagram', medium: 'social' },
    { key: 'snapchat', source: 'snapchat', medium: 'social' },
    { key: 'tiktok', source: 'tiktok', medium: 'social' },
    { key: 'x', source: 'x', medium: 'social' },
    { key: 'whatsapp', source: 'whatsapp', medium: 'social' },
    { key: 'google', source: 'google', medium: 'cpc' },
    { key: 'email', source: 'newsletter', medium: 'email' },
    { key: 'sms', source: 'sms', medium: 'sms' },
    { key: 'influencer', source: 'influencer', medium: 'referral' },
];
/** A campaign's name as a tag: lower-case words joined by dashes, Arabic kept. */
export function campaignTag(name) {
    return name
        .trim()
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, ATTRIBUTION_LIMITS.campaign);
}
//# sourceMappingURL=attribution.js.map